// Fee Management Service with Mock Data Support
// This can be easily switched to real API calls later

import {
  mockGlobalFeeSettings,
  mockVendorSpecificFees,
  mockFeeHistory,
  mockApiResponse,
  delay,
} from '../mockData';

import {
  GlobalFeeSettings,
  VendorSpecificFee,
  FeeHistory,
  UpdateGlobalSettingsPayload,
  UpdateVendorFeePayload,
  CreateVendorFeePayload,
} from '../types';

// Environment flag to switch between mock and real API
const USE_MOCK_DATA = true; // Set to false when real API is available

// Mock service functions
export const mockFeeService = {
  async getGlobalFeeSettings(): Promise<GlobalFeeSettings> {
    await delay(800); // Simulate API delay
    return mockGlobalFeeSettings;
  },

  async updateGlobalFeeSettings(payload: UpdateGlobalSettingsPayload): Promise<GlobalFeeSettings> {
    await delay(1200); // Simulate API delay
    return {
      ...mockGlobalFeeSettings,
      ...payload,
      updatedAt: new Date().toISOString(),
      updatedBy: 'Current Admin', // This would come from auth state
    };
  },

  async getVendorSpecificFees(): Promise<VendorSpecificFee[]> {
    await delay(900); // Simulate API delay
    return mockVendorSpecificFees;
  },

  async updateVendorSpecificFee(payload: UpdateVendorFeePayload): Promise<VendorSpecificFee> {
    await delay(1000); // Simulate API delay
    const existingVendor = mockVendorSpecificFees.find(v => v.restaurantId === payload.restaurantId);
    
    if (!existingVendor) {
      throw new Error(`Vendor with ID ${payload.restaurantId} not found`);
    }

    // Update the vendor's fee structure
    const updatedVendor: VendorSpecificFee = {
      ...existingVendor,
      commission: {
        ...existingVendor.commission,
        commissionRate: payload.commissionRate || existingVendor.commission.commissionRate,
        commissionType: payload.commissionType || existingVendor.commission.commissionType,
        updatedAt: new Date().toISOString(),
      },
      deliveryModel: payload.baseFee !== undefined || payload.feePerKM !== undefined ? 'custom' : existingVendor.deliveryModel,
      serviceCharge: payload.serviceChargeValue !== undefined ? 'custom' : existingVendor.serviceCharge,
    };

    return updatedVendor;
  },

  async createVendorSpecificFee(payload: CreateVendorFeePayload): Promise<VendorSpecificFee> {
    await delay(1100); // Simulate API delay
    
    const newVendor: VendorSpecificFee = {
      id: `vendor-${Date.now()}`,
      restaurantId: payload.restaurantId,
      restaurantName: payload.restaurantName,
      commission: {
        id: `comm-${Date.now()}`,
        restaurantId: payload.restaurantId,
        restaurantName: payload.restaurantName,
        commissionRate: payload.commissionRate || 15,
        commissionType: payload.commissionType || 'percentage',
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      deliveryModel: payload.baseFee !== undefined || payload.feePerKM !== undefined ? 'custom' : 'global',
      serviceCharge: payload.serviceChargeValue !== undefined ? 'custom' : 'global',
      performanceMetrics: {
        totalOrders: 0,
        totalRevenue: 0,
        commissionEarned: 0,
      },
      actions: ['Edit Fee'],
    };

    return newVendor;
  },

  async getFeeHistory(): Promise<FeeHistory[]> {
    await delay(700); // Simulate API delay
    return mockFeeHistory;
  },

  async searchRestaurants(query: string): Promise<{id: string, name: string}[]> {
    await delay(500); // Simulate API delay
    // Mock restaurant search
    const mockRestaurants = [
      { id: 'rest-004', name: 'Ocean View Restaurant' },
      { id: 'rest-005', name: 'Mountain Peak Diner' },
      { id: 'rest-006', name: 'City Center Bistro' },
      { id: 'rest-007', name: 'Riverside Cafe' },
    ];

    return mockRestaurants.filter(restaurant => 
      restaurant.name.toLowerCase().includes(query.toLowerCase())
    );
  },
};

// Real API service functions (to be implemented when backend is ready)
export const realFeeService = {
  async getGlobalFeeSettings(): Promise<GlobalFeeSettings> {
    const token = getAuthToken();
    const response = await fetch(`${process.env.REACT_APP_API_URL}/fee-management/global-settings`, {
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  },

  async updateGlobalFeeSettings(payload: UpdateGlobalSettingsPayload): Promise<GlobalFeeSettings> {
    const token = getAuthToken();
    const response = await fetch(`${process.env.REACT_APP_API_URL}/fee-management/global-settings`, {
      method: 'PUT',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  },

  async getVendorSpecificFees(): Promise<VendorSpecificFee[]> {
    const token = getAuthToken();
    const response = await fetch(`${process.env.REACT_APP_API_URL}/fee-management/vendor-specific-fees`, {
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  },

  async updateVendorSpecificFee(payload: UpdateVendorFeePayload): Promise<VendorSpecificFee> {
    const token = getAuthToken();
    const response = await fetch(`${process.env.REACT_APP_API_URL}/fee-management/vendor-specific-fees/${payload.restaurantId}`, {
      method: 'PUT',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  },

  async createVendorSpecificFee(payload: CreateVendorFeePayload): Promise<VendorSpecificFee> {
    const token = getAuthToken();
    const response = await fetch(`${process.env.REACT_APP_API_URL}/fee-management/vendor-specific-fees`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  },

  async getFeeHistory(): Promise<FeeHistory[]> {
    const token = getAuthToken();
    const response = await fetch(`${process.env.REACT_APP_API_URL}/fee-management/fee-history`, {
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  },

  async searchRestaurants(query: string): Promise<{id: string, name: string}[]> {
    const token = getAuthToken();
    const response = await fetch(`${process.env.REACT_APP_API_URL}/restaurants/search?q=${encodeURIComponent(query)}`, {
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  },
};

// Helper function to get auth token
const getAuthToken = (): string => {
  const auth = sessionStorage.getItem('authState');
  const jsonData = JSON.parse(auth || '{}');
  return jsonData?.token || '';
};

// Export the service based on environment flag
export const feeService = USE_MOCK_DATA ? mockFeeService : realFeeService;

// Export configuration for easy switching
export const config = {
  USE_MOCK_DATA,
  setUseMockData: (useMock: boolean) => {
    // This could be used to dynamically switch between mock and real API
    console.log(`Fee service switched to ${useMock ? 'mock' : 'real'} data`);
  },
};